import { createNgModuleRef, Inject, Injectable, Injector, NgModuleRef } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, mergeMap, reduce, shareReplay, tap } from 'rxjs/operators';

import { isPromise } from '@assess/shared/type-utilities';

import { IDynamicComponentMixin } from '../mixins/dynamic-component.mixin';
import { IDynamicComponentMetadata } from '../types/dynamic-component-metadata.interface';
import { IDynamicComponentModule } from '../types/dynamic-component-module.interface';
import { IDynamicComponentsModuleConfig } from '../types/dynamic-components-module-config.interface';
import { DYNAMIC_COMPONENT_MODULES } from '../types/modules.symbol';
import { OBSERVABLE_CREATOR, ObservableCreator } from '../types/observable-creator.symbol';

type RegistryEntry = NgModuleRef<IDynamicComponentModule<IDynamicComponentMixin>>;
type Registry = Map<string, RegistryEntry>;

@Injectable()
export class DynamicComponentRegistryService {
  private readonly registry$: Observable<Registry>;

  constructor(
    @Inject(DYNAMIC_COMPONENT_MODULES) readonly modules: IDynamicComponentsModuleConfig['modules'],
    @Inject(OBSERVABLE_CREATOR) readonly observableCreator: ObservableCreator,
    private readonly injector: Injector
  ) {
    this.registry$ = this.buildRegistry(modules);
  }

  get(tag: IDynamicComponentMetadata['tag']): Observable<RegistryEntry> {
    if (!tag) {
      return throwError(new Error(`Invalid tag argument: ${tag}`));
    }

    return this.registry$.pipe(
      tap((registry) => {
        if (!registry.has(tag)) {
          throw new Error(`No factory exists matching key: ${tag}`);
        }
      }),
      map((registry) => registry.get(tag) as RegistryEntry)
    );
  }

  private buildRegistry(modules: IDynamicComponentsModuleConfig['modules']) {
    return this.observableCreator(modules).pipe(
      mergeMap((module) => (isPromise(module) ? module : of(module))),

      map((module) => createNgModuleRef(module, this.injector)),
      map((moduleRef) => ({ moduleRef, componentType: moduleRef.instance.resolveComponent() })),
      reduce((registry: Registry, { componentType, moduleRef }) => {
        const metadata = (componentType as any).componentMetadata();

        if (!metadata.tag) {
          throw new Error('Tag cannot be blank');
        } else if (registry.has(metadata.tag)) {
          throw new Error(`Tag already exists in the registry: ${metadata.tag}`);
        }

        registry.set(metadata.tag, moduleRef);

        return registry;
      }, new Map()),
      shareReplay(1)
    );
  }
}
